import LiquidText, { liquidTone } from '../components/LiquidText';
import { LiquidDialog } from '../components/LiquidUI';
import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/* ============================================================
   MATRIZ DE DOCUMENTOS (SGI — ISO 9001/45001, cláusula 7.5)
   Registo dos documentos do sistema (código, revisão em vigor,
   aprovações, localização, ficheiro) + histórico de revisões.
   ============================================================ */

const TIPOS = [
    { key: 'manual', label: 'Manual' },
    { key: 'procedimento', label: 'Procedimento' },
    { key: 'instrucao', label: 'Instrução de trabalho' },
    { key: 'modelo', label: 'Modelo / Registo' },
    { key: 'plano', label: 'Plano' },
    { key: 'externo', label: 'Documento externo' },
    { key: 'outro', label: 'Outro' },
];
const TIPO_LABEL = Object.fromEntries(TIPOS.map(t => [t.key, t.label]));

const ESTADOS = {
    em_vigor: { label: 'Em vigor', bg: '#dcfce7', fg: '#166534' },
    em_revisao: { label: 'Em revisão', bg: '#fef9c3', fg: '#854d0e' },
    obsoleto: { label: 'Obsoleto', bg: '#fee2e2', fg: '#991b1b' },
};

const fmtData = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('pt-PT'); } catch { return '—'; } };

const descarregarFicheiro = async (docId, nomeBase, revisaoId) => {
    try {
        const params = revisaoId ? { revisao_id: revisaoId } : {};
        const res = await api.get(`/sistema-integrado/documentos/${docId}/ficheiro`, { responseType: 'blob', params });
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = nomeBase || 'documento';
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
    } catch (e) {
        alert(e.response?.status === 404 ? 'Este registo não tem ficheiro anexado.' : 'Erro ao descarregar ficheiro.');
    }
};

// Abrir o ficheiro em vigor numa nova aba (PDFs abrem inline; outros formatos descarregam)
const abrirFicheiro = async (docId) => {
    try {
        const res = await api.get(`/sistema-integrado/documentos/${docId}/ficheiro`, { responseType: 'blob' });
        window.open(window.URL.createObjectURL(res.data), '_blank');
    } catch (e) {
        alert(e.response?.status === 404 ? 'Este registo não tem ficheiro anexado.' : 'Erro ao abrir ficheiro.');
    }
};

// Incrementa o número de revisão preservando zeros à esquerda ("02" → "03")
const incRev = (r) => {
    const m = String(r || '').match(/^(\D*)(\d+)(\D*)$/);
    if (!m) return '01';
    return `${m[1]}${String(parseInt(m[2], 10) + 1).padStart(m[2].length, '0')}${m[3]}`;
};

/* ---------- Criar / editar documento ---------- */
function DocModal({ doc, onClose, onSaved }) {
    const novo = !doc;
    const [form, setForm] = useState({
        codigo: doc?.codigo || '', titulo: doc?.titulo || '', tipo: doc?.tipo || 'procedimento',
        processo: doc?.processo || '', revisao: doc?.revisao || '',
        data_revisao: doc?.data_revisao ? String(doc.data_revisao).substring(0, 10) : '',
        elaborado_por: doc?.elaborado_por || '', aprovado_por: doc?.aprovado_por || '',
        estado: doc?.estado || 'em_vigor', localizacao: doc?.localizacao || '',
        observacoes: doc?.observacoes || '',
        proxima_revisao: doc?.proxima_revisao ? String(doc.proxima_revisao).substring(0, 10) : '',
        visivel_colaboradores: !!doc?.visivel_colaboradores,
    });
    const [ficheiro, setFicheiro] = useState(null);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    // Anexar um ficheiro alterado a um documento que já tem ficheiro cria nova revisão ao guardar
    const criaRevisao = !novo && !!ficheiro && !!doc.ficheiro_url;

    const escolherFicheiro = (f) => {
        setFicheiro(f);
        if (!novo && doc.ficheiro_url) {
            if (f) {
                // Pré-preencher a nova revisão (nº seguinte) e a data de hoje
                upd('revisao', incRev(doc.revisao));
                upd('data_revisao', new Date().toISOString().substring(0, 10));
            } else {
                upd('revisao', doc.revisao || '');
                upd('data_revisao', doc.data_revisao ? String(doc.data_revisao).substring(0, 10) : '');
            }
        }
    };

    const guardar = async () => {
        if (!form.codigo.trim() || !form.titulo.trim()) { setErr('Código e título são obrigatórios.'); return; }
        setSaving(true); setErr('');
        try {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => {
                let val = v ?? '';
                // Ao criar revisão, os metadados guardam a revisão ANTIGA — é essa que vai
                // para o histórico; a nova entra a seguir via /nova-revisao.
                if (criaRevisao && k === 'revisao') val = doc.revisao ?? '';
                if (criaRevisao && k === 'data_revisao') val = doc.data_revisao ? String(doc.data_revisao).substring(0, 10) : '';
                fd.append(k, val);
            });
            if (ficheiro && !criaRevisao) fd.append('ficheiro', ficheiro);
            if (novo) await api.post('/sistema-integrado/documentos', fd);
            else await api.put(`/sistema-integrado/documentos/${doc.id}`, fd);

            if (criaRevisao) {
                const fd2 = new FormData();
                fd2.append('revisao', form.revisao || incRev(doc.revisao));
                fd2.append('data_revisao', form.data_revisao || new Date().toISOString().substring(0, 10));
                fd2.append('descricao_alteracao', 'Documento alterado — ficheiro substituído na edição');
                fd2.append('ficheiro', ficheiro);
                await api.post(`/sistema-integrado/documentos/${doc.id}/nova-revisao`, fd2);
            }
            onSaved(); onClose();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao guardar');
        } finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 680 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>{novo ? 'Novo documento' : `Editar ${doc.codigo}`}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>

                <div style={grid2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Código *</label>
                        <input value={form.codigo} onChange={e => upd('codigo', e.target.value)} placeholder="ex.: P_GSI_03" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Tipo</label>
                        <select value={form.tipo} onChange={e => upd('tipo', e.target.value)} style={{ ...inp, width: '100%' }}>
                            {TIPOS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select>
                    </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Título *</label>
                    <input value={form.titulo} onChange={e => upd('titulo', e.target.value)} style={{ ...inp, width: '100%' }} />
                </div>
                <div style={grid2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Processo / área</label>
                        <input value={form.processo} onChange={e => upd('processo', e.target.value)} placeholder="ex.: Compras, SST" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Estado</label>
                        <select value={form.estado} onChange={e => upd('estado', e.target.value)} style={{ ...inp, width: '100%' }}>
                            {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                    </div>
                </div>
                <div style={grid2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Revisão em vigor</label>
                        <input value={form.revisao} onChange={e => upd('revisao', e.target.value)} placeholder="ex.: 02" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Data da revisão</label>
                        <input type="date" value={form.data_revisao} onChange={e => upd('data_revisao', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                </div>
                <div style={grid2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Elaborado por</label>
                        <input value={form.elaborado_por} onChange={e => upd('elaborado_por', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Aprovado por</label>
                        <input value={form.aprovado_por} onChange={e => upd('aprovado_por', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                </div>
                <div style={grid2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Localização (rede / físico)</label>
                        <input value={form.localizacao} onChange={e => upd('localizacao', e.target.value)} placeholder="ex.: \\servidor\sistema integrado\…" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Próxima revisão planeada</label>
                        <input type="date" value={form.proxima_revisao} onChange={e => upd('proxima_revisao', e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Observações</label>
                    <textarea value={form.observacoes} onChange={e => upd('observacoes', e.target.value)} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                </div>
                {/* O que os trabalhadores consultam no telemóvel (só a revisão em vigor) */}
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '0.85rem', color: '#334155', cursor: 'pointer', marginBottom: 10 }}>
                    <input type="checkbox" checked={!!form.visivel_colaboradores}
                        onChange={e => upd('visivel_colaboradores', e.target.checked)} style={{ marginTop: 3 }} />
                    <span><strong><LiquidText>👷 Visível aos colaboradores</LiquidText></strong> — aparece no separador Documentos da app no telemóvel,
                        enquanto estiver em vigor e tiver ficheiro.</span>
                </label>
                <div style={{ marginBottom: 6 }}>
                    <label data-liquid-label="true" style={lbl}>{novo ? 'Ficheiro (opcional)' : 'Documento'}</label>
                    {!novo && doc.ficheiro_url && (
                        <div style={{ marginBottom: 8 }}>
                            <button data-liquid-secondary="true" type="button" onClick={() => abrirFicheiro(doc.id)} style={btnSec}><LiquidText>
                                📄 Abrir documento (rev. </LiquidText>{doc.revisao || '—'})
                            </button>
                        </div>
                    )}
                    <input type="file" onChange={e => escolherFicheiro(e.target.files?.[0] || null)} style={{ fontSize: '0.85rem' }} />
                    {criaRevisao && (
                        <div data-liquid-notice="true" data-liquid-tone={liquidTone('#fef9c3')} style={{ fontSize: '0.8rem', color: '#854d0e', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 8, padding: '6px 10px', marginTop: 6 }}>
                            Ao guardar, a revisão atual ({doc.revisao || '—'}) é arquivada no histórico e o documento
                            passa à revisão <strong>{form.revisao || incRev(doc.revisao)}</strong> com o novo ficheiro.
                        </div>
                    )}
                    {!novo && doc.ficheiro_url && !ficheiro && (
                        <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 4 }}>
                            Se anexar aqui um ficheiro alterado, é criada uma nova revisão automaticamente ao guardar.
                        </div>
                    )}
                </div>

                {err && <div style={{ color: '#e53e3e', margin: '10px 0', fontSize: '0.85rem' }}>{err}</div>}
                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={guardar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>{saving ? 'A guardar…' : 'Guardar'}</button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Nova revisão ---------- */
function NovaRevisaoModal({ doc, onClose, onSaved }) {
    const [revisao, setRevisao] = useState('');
    const [dataRevisao, setDataRevisao] = useState(new Date().toISOString().substring(0, 10));
    const [descricao, setDescricao] = useState('');
    const [ficheiro, setFicheiro] = useState(null);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    const guardar = async () => {
        if (!revisao.trim()) { setErr('Indique o número da nova revisão.'); return; }
        setSaving(true); setErr('');
        try {
            const fd = new FormData();
            fd.append('revisao', revisao);
            fd.append('data_revisao', dataRevisao);
            fd.append('descricao_alteracao', descricao);
            if (ficheiro) fd.append('ficheiro', ficheiro);
            await api.post(`/sistema-integrado/documentos/${doc.id}/nova-revisao`, fd);
            onSaved(); onClose();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao criar revisão');
        } finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 520 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>Nova revisão — {doc.codigo}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: 0 }}>
                    A revisão atual ({doc.revisao || '—'}) é arquivada no histórico e o documento passa à nova revisão.
                </p>
                <div style={grid2}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Nova revisão *</label>
                        <input value={revisao} onChange={e => setRevisao(e.target.value)} placeholder="ex.: 03" style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Data</label>
                        <input type="date" value={dataRevisao} onChange={e => setDataRevisao(e.target.value)} style={{ ...inp, width: '100%' }} />
                    </div>
                </div>
                <div style={{ marginBottom: 10 }}>
                    <label data-liquid-label="true" style={lbl}>Descrição da alteração</label>
                    <textarea value={descricao} onChange={e => setDescricao(e.target.value)} rows={3} style={{ ...inp, width: '100%', resize: 'vertical' }} placeholder="O que mudou nesta revisão?" />
                </div>
                <div style={{ marginBottom: 6 }}>
                    <label data-liquid-label="true" style={lbl}>Ficheiro da nova revisão (opcional)</label>
                    <input type="file" onChange={e => setFicheiro(e.target.files?.[0] || null)} style={{ fontSize: '0.85rem' }} />
                </div>
                {err && <div style={{ color: '#e53e3e', margin: '10px 0', fontSize: '0.85rem' }}>{err}</div>}
                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={guardar} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>{saving ? 'A guardar…' : 'Criar revisão'}</button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Histórico de revisões ---------- */
function HistoricoModal({ doc, onClose }) {
    const [revisoes, setRevisoes] = useState(null);
    useEffect(() => {
        api.get(`/sistema-integrado/documentos/${doc.id}/revisoes`)
            .then(r => setRevisoes(r.data || []))
            .catch(() => setRevisoes([]));
    }, [doc.id]);

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>Histórico — {doc.codigo}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                <div data-liquid-notice="true" data-liquid-tone={liquidTone('#f0fdf4')} style={{ padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, marginBottom: 12, fontSize: '0.85rem', color: '#166534' }}>
                    <strong>Em vigor:</strong> rev. {doc.revisao || '—'} · {fmtData(doc.data_revisao)}
                </div>
                {revisoes === null ? <p style={{ color: '#64748b' }}>A carregar…</p>
                    : revisoes.length === 0 ? <p style={{ color: '#94a3b8', fontSize: '0.88rem' }}>Sem revisões arquivadas.</p>
                        : revisoes.map(rv => (
                            <div key={rv.id} style={{ borderTop: '1px solid #f1f5f9', padding: '10px 2px', fontSize: '0.88rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                                    <strong style={{ color: '#334155' }}>Rev. {rv.revisao || '—'}</strong>
                                    <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>{fmtData(rv.data_revisao)} · arquivada {fmtData(rv.criado_em)}</span>
                                    {rv.ficheiro_url && (
                                        <button data-liquid-secondary="true" onClick={() => descarregarFicheiro(doc.id, `${doc.codigo}_rev${rv.revisao || ''}`, rv.id)} style={{ ...btnSec, padding: '3px 10px', fontSize: '0.78rem' }}><LiquidText>⬇</LiquidText></button>
                                    )}
                                </div>
                                {rv.descricao_alteracao && <div style={{ color: '#64748b', marginTop: 4 }}>{rv.descricao_alteracao}</div>}
                            </div>
                        ))}
            </div>
        </LiquidDialog>
    );
}

/* ---------- Componente principal ---------- */
const MatrizDocumentos = () => {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pesquisa, setPesquisa] = useState('');
    const [tipoFiltro, setTipoFiltro] = useState('');
    const [estadoFiltro, setEstadoFiltro] = useState('');
    const [editar, setEditar] = useState(null);      // null | {doc|undefined}
    const [novaRev, setNovaRev] = useState(null);
    const [historico, setHistorico] = useState(null);

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/sistema-integrado/documentos');
            setDocs(r.data || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { carregar(); }, [carregar]);

    const eliminar = async (d) => {
        if (!window.confirm(`Eliminar o documento "${d.codigo} — ${d.titulo}" e o seu histórico?`)) return;
        try { await api.delete(`/sistema-integrado/documentos/${d.id}`); carregar(); }
        catch { alert('Erro ao eliminar'); }
    };

    let visiveis = docs;
    if (tipoFiltro) visiveis = visiveis.filter(d => d.tipo === tipoFiltro);
    if (estadoFiltro) visiveis = visiveis.filter(d => d.estado === estadoFiltro);
    if (pesquisa.trim()) {
        const t = pesquisa.trim().toLowerCase();
        visiveis = visiveis.filter(d =>
            (d.codigo || '').toLowerCase().includes(t) || (d.titulo || '').toLowerCase().includes(t) ||
            (d.processo || '').toLowerCase().includes(t) || (d.aprovado_por || '').toLowerCase().includes(t));
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Pesquisar</label>
                        <input value={pesquisa} onChange={e => setPesquisa(e.target.value)} placeholder="Código, título, processo…" style={{ ...inp, width: 220 }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Tipo</label>
                        <select value={tipoFiltro} onChange={e => setTipoFiltro(e.target.value)} style={inp}>
                            <option value="">Todos</option>
                            {TIPOS.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Estado</label>
                        <select value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value)} style={inp}>
                            <option value="">Todos</option>
                            {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                        </select>
                    </div>
                </div>
                <button data-liquid-primary="true" onClick={() => setEditar({})} style={btnPri}>+ Novo documento</button>
            </div>

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p>
                : visiveis.length === 0 ? (
                    <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>
                        {docs.length === 0 ? 'Sem documentos registados. Comece por registar o manual e os procedimentos do SGI.' : 'Nenhum documento corresponde aos filtros.'}
                    </div>
                ) : (
                    <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
                        <table className="standard-table">
                            <thead>
                                <tr>
                                    <th>Código</th>
                                    <th>Título</th>
                                    <th>Tipo</th>
                                    <th>Processo</th>
                                    <th style={{ textAlign: 'center' }}>Rev.</th>
                                    <th>Data rev.</th>
                                    <th>Estado</th>
                                    <th>Próx. revisão</th>
                                    <th style={{ textAlign: 'right' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visiveis.map(d => {
                                    const es = ESTADOS[d.estado] || ESTADOS.em_vigor;
                                    return (
                                        <tr key={d.id}>
                                            <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{d.codigo}</td>
                                            <td style={{ maxWidth: 300 }}>
                                                <LiquidText>{d.titulo}</LiquidText>
                                                {d.visivel_colaboradores && (
                                                    <span data-liquid-tone={liquidTone('#e0f2fe')} data-liquid-badge="true" title="Visível aos colaboradores no separador Documentos do telemóvel"
                                                        style={{ ...badge, background: '#e0f2fe', color: '#075985', marginLeft: 6, fontSize: '0.68rem' }}><LiquidText>👷 app</LiquidText></span>
                                                )}
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>{TIPO_LABEL[d.tipo] || d.tipo}</td>
                                            <td>{d.processo || '—'}</td>
                                            <td style={{ textAlign: 'center', fontWeight: 600 }}>
                                                {d.revisao || '—'}
                                                {d.num_revisoes > 0 && <span style={{ color: '#94a3b8', fontWeight: 400 }} title={`${d.num_revisoes} revisão(ões) arquivada(s)`}> ({d.num_revisoes})</span>}
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>{fmtData(d.data_revisao)}</td>
                                            <td><span data-liquid-tone={liquidTone(es.bg)} data-liquid-badge="true" style={{ ...badge, background: es.bg, color: es.fg }}><LiquidText>{es.label}</LiquidText></span></td>
                                            <td style={{ whiteSpace: 'nowrap' }}>{fmtData(d.proxima_revisao)}</td>
                                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                {d.ficheiro_url && (
                                                    <>
                                                        <button data-liquid-secondary="true" onClick={() => abrirFicheiro(d.id)} style={{ ...btnSec, ...mini }} title="Abrir documento em vigor"><LiquidText>📄</LiquidText></button>
                                                        <button data-liquid-secondary="true" onClick={() => descarregarFicheiro(d.id, `${d.codigo}_rev${d.revisao || ''}`)} style={{ ...btnSec, ...mini }} title="Descarregar ficheiro em vigor"><LiquidText>⬇</LiquidText></button>
                                                    </>
                                                )}
                                                <button data-liquid-primary="true" onClick={() => setNovaRev(d)} style={{ ...btnPri, ...mini }} title="Registar nova revisão (arquiva a atual)">Nova revisão</button>
                                                <button data-liquid-secondary="true" onClick={() => setHistorico(d)} style={{ ...btnSec, ...mini }} title="Histórico de revisões">Histórico</button>
                                                <button data-liquid-secondary="true" onClick={() => setEditar({ doc: d })} style={{ ...btnSec, ...mini }} title="Editar">Editar</button>
                                                <button data-liquid-secondary="true" onClick={() => eliminar(d)} style={{ ...btnSec, ...mini, color: '#e53e3e' }} title="Eliminar"><DeleteIcon /></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

            {editar && <DocModal doc={editar.doc} onClose={() => setEditar(null)} onSaved={carregar} />}
            {novaRev && <NovaRevisaoModal doc={novaRev} onClose={() => setNovaRev(null)} onSaved={carregar} />}
            {historico && <HistoricoModal doc={historico} onClose={() => setHistorico(null)} />}
        </div>
    );
};

/* ---------- estilos ---------- */
const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const mini = { padding: '5px 10px', marginLeft: 6, fontSize: '0.78rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const modalHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 };

export default MatrizDocumentos;
